const groupPrefix = createApiRoute(ApiModule.PRODUCTION_PLANNING, ApiModule.EMPLOYEE, ApiDataModel.PRODUCTION_PLANS)
const groupPrefixEmployee = createApiRoute(ApiModule.PRODUCTION_PLANNING, ApiModule.EMPLOYEE)

export const apiProductionPlans = {
	fetchProductionPlans: async (
		date?: Date,
		page: number = 1,
		perPage: number = 25,
	): Promise<IProductionPlanModel[]> => {
		const { data } = await apiCommon<IProductionPlan[]>(`${groupPrefix}`, {
			method: 'GET',
			...createQuery({ page, perPage }, { date: date ? dateToFilterFormat(date) : undefined }),
		})

		return data ? data.map(d => ProductionPlan(d)) : []
	},

	fetchProductionPlanDetails: async (id: number): Promise<IProductionPlanDetailsModel> => {
		const { data } = await apiCommon<IProductionPlanDetails>(`${groupPrefix}/${id}`, { method: 'GET' })

		return ProductionPlanDetails(data)
	},

	fetchProductionPlanOrderedMeals: async (productionPlanId: number): Promise<IProductionPlanOrderedMealModel[]> => {
		const { data } = await apiCommon<IProductionPlanOrderedMeal[]>(
			`${groupPrefix}/${productionPlanId}/ordered-meals`,
			{
				method: 'GET',
			},
		)
		return data.map(el => ProductionPlanOrderedMeal(el))
	},

	fetchProductionPlanOrderedMealsCheckChanges: async (
		productionPlanId: number,
	): Promise<IProductionPlanOrderedMealModel[]> => {
		const { data } = await apiCommon<IProductionPlanOrderedMeal[]>(
			`${groupPrefix}/${productionPlanId}/ordered-meals/check-changes`,
			{
				method: 'GET',
			},
		)
		return data.map(el => ProductionPlanOrderedMeal(el))
	},

	confirmProductionCheckChanges: async (changeId: number) => {
		await apiCommon(`${groupPrefixEmployee}/production-plans/ordered-meals/confirm-change/${changeId}`, {
			method: 'POST',
		})
	},

	fetchProductionPlanLatest: async (
		start_date?: Date,
		end_date?: Date,
		status?: EProductionPlansStatus,
		is_final?: boolean | null,
		page?: number,
		perPage?: number,
		sortDirection: EApiSortType = EApiSortType.ASC,
	): Promise<IApiResponseModel<IProductionPlanModel[]>> => {
		const { data, meta } = await apiCommon<IProductionPlan[]>(`${groupPrefix}/latest`, {
			method: 'GET',
			...createQuery(
				null,
				{
					status: status === undefined ? undefined : `${status}`,
					is_final: is_final === undefined || is_final === null ? undefined : `${is_final}`,
				},
				{ 'production_plans.date': sortDirection },
				{
					start_date: start_date ? dateToFilterFormat(start_date) : undefined,
					end_date: end_date ? dateToFilterFormat(end_date) : undefined,
					page: page !== undefined ? `${page}` : undefined,
					per_page: perPage !== undefined ? `${perPage}` : undefined,
				},
			),
		})

		return { data: data ? data.map(d => ProductionPlan(d)) : [], meta }
	},

	generateProductionPlan: async (date: Date): Promise<IProductionPlanDetailsModel> => {
		const { data } = await apiCommon<IProductionPlanDetails>(`${groupPrefix}`, {
			method: 'POST',
			body: { date: dateToFilterFormat(date), location_id: 1 },
		})

		return ProductionPlanDetails(data)
	},

	generateProductionPlanManual: async (date: Date): Promise<IApiResponseModel<IProductionPlanModel>> => {
		const { data } = await apiCommon<IProductionPlan>(`${groupPrefix}/manual`, {
			method: 'POST',
			body: { date: dateToFilterFormat(date), location_id: 1 },
		})

		return { data: ProductionPlan(data) }
	},

	generateSimulation: async (planID: number): Promise<IApiResponseModel<IProductionSimulationModel>> => {
		const { data } = await apiCommon<IProductionSimulation>(`${groupPrefix}/${planID}/simulations`, {
			method: 'POST',
		})

		return { data: ProductionSimulation(data) }
	},

	fetchProductionPlanMeal: async (
		planID: number,
		mealID: number,
	): Promise<IApiResponseModel<IProductionPlanMealModel>> => {
		const { data } = await apiCommon<IProductionPlanMeal>(`${groupPrefix}/${planID}/meals/${mealID}`, {
			method: 'GET',
		})

		return { data: ProductionPlanMeals(data) }
	},

	fetchCRMMeals: async (date: Date): Promise<IApiResponseModel<IDietMeal[]>> => {
		const { data } = await createProxy<IDietMeal[]>(
			`/dietary/api/meals?filter[date]=${dateToFilterFormat(date)}&sort=-meal_type,id&filter[dietday.status]=public`,
		)

		return { data: data ?? [] }
	},

	searchCRMMeals: async (search: string): Promise<IApiResponseModel<IDietMeal[]>> => {
		const params = new URLSearchParams()
		params.set('filter[name]', search.trim())

		const { data } = await createProxy<IDietMeal[]>(`/dietary/api/meals?${params.toString()}`)

		return { data: data ?? [] }
	},

	fetchInternalOrdersMeals: async (date: Date): Promise<IApiResponseModel<IInternalOrderMeal[]>> => {
		const { data } = await createProxy<IInternalOrderMeal[]>(
			`/order/api/employee/internal-orders/mes-delivery?date=${dateToFilterFormat(date)}`,
		)

		return { data: data ?? [] }
	},

	fetchAddonsMeals: async (): Promise<IApiResponseModel<IDietMeal[]>> => {
		const { data } = await createProxy<IDietMeal[]>(
			'/dietary/api/addons-meals?filter[addon_category]=null&sort=availability',
		)

		return { data: data ?? [] }
	},

	fetchAddonsMealsByCategory: async (addonCategory: string): Promise<IApiResponseModel<IDietMeal[]>> => {
		const mealSubkindFilter = addonCategory === 'cookie' ? '&filter[meal_subkind]=addon' : ''
		const { data } = await createProxy<IDietMeal[]>(
			`/dietary/api/addons-meals?sort=availability${mealSubkindFilter}`,
		)

		return { data: data ?? [] }
	},

	fetchInternalOrders: async (date: Date): Promise<IInternalOrderModel[]> => {
		const { data } = await createProxy<IInternalOrder[]>(
			`order/api/employee/internal-orders/mes?date=${dateToFilterFormat(date)}`,
		)
		return data ? data.map(d => InternalOrder(d)) : []
	},

	addMealsToProductionPlan: async (
		planID: number,
		mealsVariants: Array<{ crm_meal_variant_id: number; quantity: number; sales_quantity: number }>,
	): Promise<IApiResponseModel<unknown>> => {
		const { data } = await apiCommon(`${groupPrefix}/${planID}/meals`, {
			method: 'POST',
			body: { meals_variants: mealsVariants },
		})

		await apiProductionPlans.fetchProductionPlanDetails(planID)

		return { data }
	},

	exportProductionPlan: async (planID: number) =>
		await apiCommon<Blob>(`${groupPrefix}/${planID}/meals/export`, {
			method: 'GET',
			isFileDownload: true,
		}),

	updateProductionPlanMeals: async (
		planID: number,
		mealsVariants: Array<{ crm_meal_variant_id: number; sales_quantity: number; sales_in_fridge: boolean }>,
	): Promise<void> => {
		await apiCommon(`${groupPrefix}/${planID}/meals/update`, {
			method: 'PUT',
			body: { meals_variants: mealsVariants },
		})
	},

	synchronizeProductsWithCRM: async (productionPlanId: number) => {
		await apiCommon(`${groupPrefixEmployee}/production-check-changes/${productionPlanId}`, {
			method: 'POST',
		})
	},
}
